import React from 'react';
import { 
  Recycle, 
  Leaf, 
  Zap, 
  DollarSign, 
  TrendingUp, 
  Droplets, 
  Sparkles,
  Lightbulb,
  ArrowRight,
  CheckCircle,
  Coffee,
  Sprout,
  Brain,
  Globe
} from 'lucide-react';
import { motion } from 'framer-motion';

const CoffeeByproducts: React.FC = () => {
  return (
    <div className="bg-white rounded-xl shadow-md p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <motion.div
            animate={{ rotate: [0, 5, -5, 0] }}
            transition={{ duration: 4, repeat: Infinity }}
            className="bg-green-100 p-2 rounded-lg"
          >
            <Brain className="h-5 w-5 text-green-600" />
          </motion.div>
          <div>
            <h2 className="text-xl font-semibold">AVN-AI Innovations</h2>
            <p className="text-sm text-gray-500">Scalable agri-tech solutions with global potential</p>
          </div>
        </div>
        <div className="flex items-center gap-2 text-sm text-green-600">
          <Globe className="h-4 w-4" />
          <span>Global Licensing Potential</span>
        </div>
      </div>

      {/* Featured Products */}
      <div className="grid md:grid-cols-2 gap-6 mb-6">
        {/* Coffee Cherry Syrup */}
        <div className="bg-gradient-to-br from-amber-50 to-amber-100 rounded-lg p-5 relative overflow-hidden">
          <div className="absolute top-0 right-0 w-24 h-24 opacity-15">
            <Brain className="w-full h-full text-amber-900" />
          </div>
          
          <h3 className="text-lg font-bold text-amber-900 mb-2 flex items-center gap-2">
            <Zap className="h-5 w-5 text-amber-600" />
            AVN Traceability Engine
          </h3>
          
          <p className="text-amber-800 mb-4 text-sm">
            Proprietary AI system that verifies ethical practices and creates transparent supply chains.
          </p>
          
          <div className="space-y-2 mb-4">
            <div className="flex items-center gap-2 text-sm">
              <CheckCircle className="h-4 w-4 text-green-600" />
              <span className="text-amber-800">Blockchain-verified transactions</span>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <CheckCircle className="h-4 w-4 text-green-600" />
              <span className="text-amber-800">Real-time impact metrics</span>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <CheckCircle className="h-4 w-4 text-green-600" />
              <span className="text-amber-800">Licensable to other producer groups</span>
            </div>
          </div>
          
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-amber-900">
              <DollarSign className="h-4 w-4" />
              <span className="font-bold">$250K</span>
              <span className="text-xs">annual revenue potential</span>
            </div>
            <div className="flex items-center gap-1 text-green-600 text-sm">
              <TrendingUp className="h-4 w-4" />
              <span>Scalable globally</span>
            </div>
          </div>
        </div>

        {/* Cocoa Husk Mulch */}
        <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-lg p-5 relative overflow-hidden">
          <div className="absolute top-0 right-0 w-24 h-24 opacity-10">
            <Globe className="w-full h-full text-green-900" />
          </div>
          
          <h3 className="text-lg font-bold text-green-900 mb-2 flex items-center gap-2">
            <Brain className="h-5 w-5 text-green-600" />
            Dynamic Revenue Model
          </h3>
          
          <p className="text-green-800 mb-4 text-sm">
            Innovative financial system that ensures fair compensation through transparent revenue sharing.
          </p>
          
          <div className="space-y-2 mb-4">
            <div className="flex items-center gap-2 text-sm">
              <CheckCircle className="h-4 w-4 text-green-600" />
              <span className="text-green-800">Automated payment distribution</span>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <CheckCircle className="h-4 w-4 text-green-600" />
              <span className="text-green-800">Transparent profit sharing</span>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <CheckCircle className="h-4 w-4 text-green-600" />
              <span className="text-green-800">Adaptable to various value chains</span>
            </div>
          </div>
          
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-green-900">
              <DollarSign className="h-4 w-4" />
              <span className="font-bold">25-50%</span>
              <span className="text-xs">farmer revenue share</span>
            </div>
            <div className="flex items-center gap-1 text-green-600 text-sm">
              <TrendingUp className="h-4 w-4" />
              <span>Proven impact model</span>
            </div>
          </div>
        </div>
      </div>

      {/* Innovation Pipeline */}
      <div className="mb-6">
        <h3 className="font-medium mb-3 flex items-center gap-2">
          <Lightbulb className="h-5 w-5 text-avn-purple-600" />
          <span>Technology Pipeline</span>
        </h3>
        
        <div className="space-y-3">
          <div className="bg-gray-50 p-3 rounded-lg">
            <div className="flex items-center justify-between mb-1">
              <h4 className="font-medium">AVN Wallet System</h4>
              <div className="bg-blue-100 text-blue-700 text-xs px-2 py-0.5 rounded-full">
                In Development
              </div>
            </div>
            <p className="text-sm text-gray-600 mb-2">
              Secure digital wallet infrastructure for farmers to receive, track, and manage payments with full transparency.
            </p>
            <div className="w-full bg-gray-200 rounded-full h-1.5">
              <div className="bg-blue-600 h-1.5 rounded-full" style={{ width: '70%' }}></div>
            </div>
            <div className="flex justify-between text-xs text-gray-500 mt-1">
              <span>Research</span>
              <span>Testing</span>
              <span>Production</span>
              <span>Market</span>
            </div>
          </div>
          
          <div className="bg-gray-50 p-3 rounded-lg">
            <div className="flex items-center justify-between mb-1">
              <h4 className="font-medium">Producer Verification Protocol</h4>
              <div className="bg-amber-100 text-amber-700 text-xs px-2 py-0.5 rounded-full">
                Pilot Phase
              </div>
            </div>
            <p className="text-sm text-gray-600 mb-2">
              Decentralized verification system that authenticates producer practices and creates immutable proof of ethical production.
            </p>
            <div className="w-full bg-gray-200 rounded-full h-1.5">
              <div className="bg-amber-500 h-1.5 rounded-full" style={{ width: '40%' }}></div>
            </div>
            <div className="flex justify-between text-xs text-gray-500 mt-1">
              <span>Research</span>
              <span>Testing</span>
              <span>Production</span>
              <span>Market</span>
            </div>
          </div>
        </div>
      </div>

      {/* Impact Metrics */}
      <div className="bg-avn-purple-50 p-4 rounded-lg">
        <h3 className="font-medium mb-3 flex items-center gap-2">
          <Sparkles className="h-5 w-5 text-avn-purple-600" />
          <span>Technology Impact</span>
        </h3>
        
        <div className="grid grid-cols-3 gap-4 text-center">
          <div>
            <div className="text-2xl font-bold text-avn-purple-700">100%</div>
            <div className="text-xs text-gray-600">Supply Chain Visibility</div>
          </div>
          <div>
            <div className="text-2xl font-bold text-avn-purple-700">45%</div>
            <div className="text-xs text-gray-600">Income Increase</div>
          </div>
          <div>
            <div className="text-2xl font-bold text-avn-purple-700">3</div>
            <div className="text-xs text-gray-600">Licensable Technologies</div>
          </div>
        </div>
      </div>

      {/* Call to Action */}
      <div className="mt-6 pt-6 border-t border-gray-100">
        <div className="flex items-center justify-between">
          <p className="text-sm text-gray-600">
            Interested in licensing our technology for your producer network?
          </p>
          <button className="text-avn-purple-600 hover:text-avn-purple-700 flex items-center gap-1 text-sm">
            <span>Request Technology Demo</span>
            <ArrowRight className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default CoffeeByproducts;